import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SchwabDataService } from '../../../services/schwab-data.service';
import { PlotlyService } from '../../../services/plotly.service';
import { OptionChainRequest } from '../../../models/OptionChainRequest';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-option-oint-surface',
  templateUrl: './option-oint-surface.component.html',
  styleUrls: ['./option-oint-surface.component.scss'],
})
export class OptionOintSurfaceComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private schwabData: SchwabDataService,
    private plot: PlotlyService,
    private messageService: MessageService
  ) {
    this.optionForm = this.fb.group({
      symbol: ['SPY', Validators.required],
      includeCalls: [true],
      includePuts: [true],
      fromDate: [this.getTodayISO()],            // default: today
      toDate: [this.getDate30DaysFromToday()],   // default: +30 days
      strikeCount: [20],
      nearestExpirations: [6],
    });

    this.dataDisplayForm = this.fb.group({
      showRawData: [false],
      showCalls: [true],
      showPuts: [true],
      showNet: [true],
      showPlane: [true],              // default: ON
      showCombinedTooltips: [false],  // default: OFF
    });
  }

  optionForm: FormGroup;
  dataDisplayForm: FormGroup;

  title: string = 'Option Chain Open Interest Surface';

  loading = false;
  errorMsg = '';
  showRawDataBool = false;
  rawChain: any | null = null;

  graphData: any;
  graphLayout: any;

  // Keep latest surfaces for toggling without refetching
  private lastX: number[] | null = null;
  private lastY: number[] | null = null;
  private lastZCalls: number[][] | null = null;
  private lastZPuts: number[][] | null = null;
  private lastZNet: number[][] | null = null;
  private lastUnderlyingPrice: number | null = null;
  private lastTitleSuffix: string = '';
  private lastCallsRaw: number[][] | null = null; // raw positive calls grid
  private lastPutsRaw: number[][] | null = null;  // raw positive puts grid

  ngOnInit(): void {
    this.dataDisplayForm.get('showRawData')?.valueChanges.subscribe((v) => {
      this.showRawDataBool = v;
    });

    this.dataDisplayForm.get('showCalls')?.valueChanges.subscribe(() => this.updateGraphData());
    this.dataDisplayForm.get('showPuts')?.valueChanges.subscribe(() => this.updateGraphData());
    this.dataDisplayForm.get('showNet')?.valueChanges.subscribe(() => this.updateGraphData());
    this.dataDisplayForm.get('showPlane')?.valueChanges.subscribe(() => this.updateGraphData());
    this.dataDisplayForm.get('showCombinedTooltips')?.valueChanges.subscribe(() => this.updateGraphData());
  }

  submit(): void {
    this.errorMsg = '';
    if (!this.optionForm.valid) {
      this.showErrorToast('Form invalid');
      return;
    }

    const { symbol, includeCalls, includePuts, fromDate, toDate } = this.optionForm.value as {
      symbol: string;
      includeCalls: boolean;
      includePuts: boolean;
      fromDate?: string | null;
      toDate?: string | null;
    };

    this.loading = true;

    if (!this.isIndexSymbol(symbol)) {
      const req: OptionChainRequest = {
        symbol: symbol.trim().toUpperCase(),
        fromDate: fromDate || undefined,
        toDate: toDate || undefined
      };
      this.fetchChain(req, symbol, includeCalls, includePuts);
      return;
    }

    const candidates = this.buildIndexCandidates(symbol);
    this.tryFetchIndexSequential(candidates, includeCalls, includePuts, 0);
  }

  private fetchChain(
    req: OptionChainRequest,
    displaySymbol: string,
    includeCalls: boolean,
    includePuts: boolean
  ) {
    this.schwabData.getOptionChain(req).subscribe({
      next: (chain) => {
        try {
          this.rawChain = chain;
          const { x, y, zCalls, zPuts, zNet, callsRaw, putsRaw } =
            this.buildOISurfaces(chain, includeCalls, includePuts);

          this.lastX = x;
          this.lastY = y;
          this.lastZCalls = zCalls;
          this.lastZPuts = zPuts;
          this.lastZNet = zNet;
          this.lastUnderlyingPrice = Number(chain?.underlyingPrice);
          this.lastTitleSuffix = displaySymbol;
          this.lastCallsRaw = callsRaw;
          this.lastPutsRaw = putsRaw;

          this.updateGraphData();
        } catch (e: any) {
          this.showErrorToast(e?.message ?? 'Failed to parse option chain response');
        } finally {
          this.loading = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.errorMsg = err.message;
        this.showErrorToast(err.message);
      },
    });
  }

  private isIndexSymbol(symbol: string): boolean {
    return ['SPX', 'NDX', 'RUT'].includes(symbol.trim().toUpperCase());
  }

  private buildIndexCandidates(symbol: string): string[] {
    symbol = symbol.trim().toUpperCase();
    if (symbol === 'SPX') return ['SPXW', 'SPX'];
    if (symbol === 'NDX') return ['NDX', 'NDXP'];
    if (symbol === 'RUT') return ['RUT'];
    return [symbol];
  }

  private tryFetchIndexSequential(
    candidates: string[],
    includeCalls: boolean,
    includePuts: boolean,
    idx: number
  ) {
    if (idx >= candidates.length) {
      this.loading = false;
      this.showErrorToast('None of the index candidates returned valid data');
      return;
    }

    const candidate = candidates[idx];
    const req: OptionChainRequest = {
      symbol: candidate.trim().toUpperCase(),
      fromDate: undefined,
      toDate: undefined
    };

    this.schwabData.getOptionChain(req).subscribe({
      next: (chain) => {
        try {
          this.rawChain = chain;
          const { x, y, zCalls, zPuts, zNet, callsRaw, putsRaw } =
            this.buildOISurfaces(chain, includeCalls, includePuts);

          this.lastX = x;
          this.lastY = y;
          this.lastZCalls = zCalls;
          this.lastZPuts = zPuts;
          this.lastZNet = zNet;
          this.lastUnderlyingPrice = Number(chain?.underlyingPrice);
          this.lastTitleSuffix = candidate;
          this.lastCallsRaw = callsRaw;
          this.lastPutsRaw = putsRaw;

          this.updateGraphData();
          this.loading = false;
        } catch (e: any) {
          this.showErrorToast(e?.message ?? 'Failed to parse option chain response');
          this.loading = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.errorMsg = err.message;
        this.tryFetchIndexSequential(candidates, includeCalls, includePuts, idx + 1);
      },
    });
  }

  private showErrorToast(msg: string) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: msg,
    });
  }

  private updateGraphData(): void {
    if (!this.lastX || !this.lastY || !this.lastZCalls || !this.lastZPuts || !this.lastZNet) {
      return;
    }

    const showCalls = !!this.dataDisplayForm.get('showCalls')?.value;
    const showPuts = !!this.dataDisplayForm.get('showPuts')?.value;
    const showNet = !!this.dataDisplayForm.get('showNet')?.value;
    const showPlane = !!this.dataDisplayForm.get('showPlane')?.value;
    const showCombinedTooltips = !!this.dataDisplayForm.get('showCombinedTooltips')?.value;

    const traces: any[] = [];

    if (showCalls) {
      traces.push({
        x: this.lastX,
        y: this.lastY,
        z: this.lastZCalls,
        type: 'surface',
        name: 'Calls OI',
        colorscale: 'Blues',
        showscale: false,
        opacity: 0.95,
        hovertemplate: 'Strike=%{x}<br>DTE=%{y}<br>Calls OI=%{z}<extra></extra>'
      });
    }

    if (showPuts) {
      traces.push({
        x: this.lastX,
        y: this.lastY,
        z: this.lastZPuts, // negative
        type: 'surface',
        name: 'Puts OI (inverted)',
        colorscale: 'Reds',
        showscale: false,
        opacity: 0.95,
        hovertemplate: 'Strike=%{x}<br>DTE=%{y}<br>Puts OI=%{z}<extra></extra>'
      });
    }

    // Net surface
    const netSurfaceTrace: any = {
      x: this.lastX,
      y: this.lastY,
      z: this.lastZNet,
      type: 'surface',
      name: 'Net OI (Calls - Puts)',
      colorscale: 'Greens',
      showscale: false,
      opacity: 0.8
    };

    if (showNet) {
      if (showCombinedTooltips && this.lastCallsRaw && this.lastPutsRaw) {
        // Disable surface hover to avoid interpolated values
        netSurfaceTrace.hoverinfo = 'skip';

        // Add invisible scatter3d markers at exact grid cells to show precise tooltips
        const hoverPoints = this.buildNetHoverPoints(
          this.lastX,
          this.lastY,
          this.lastZNet,
          this.lastCallsRaw,
          this.lastPutsRaw
        );
        traces.push(netSurfaceTrace);
        traces.push(hoverPoints);
      } else {
        // Fallback: keep surface hover
        netSurfaceTrace.hovertemplate = 'Strike=%{x}<br>DTE=%{y}<br>Net OI=%{z}<extra></extra>';
        traces.push(netSurfaceTrace);
      }
    }

    if (showPlane) {
      const planeTrace = this.buildUnderlyingPlaneMesh(
        this.lastUnderlyingPrice ?? undefined,
        this.lastX,
        this.lastY,
        this.lastZNet!
      );
      if (planeTrace) traces.push(planeTrace);
    }

    this.graphData = traces;

    this.graphLayout = {
      title: `${this.title} - ${this.lastTitleSuffix}`,
      scene: {
        xaxis: { title: 'Strike' },
        yaxis: { title: 'Days to Expiration (DTE)' },
        zaxis: { title: 'Open Interest (Calls +, Puts -)' },
      },
      margin: { l: 0, r: 0, b: 0, t: 40 },
    };
  }

  // Build precise hover points for Net surface to prevent interpolation
  private buildNetHoverPoints(
    x: number[],
    y: number[],
    zNet: number[][],
    callsRaw: number[][],
    putsRaw: number[][]
  ) {
    const X: number[] = [];
    const Y: number[] = [];
    const Z: number[] = [];
    const custom: [number, number][] = [];

    for (let yi = 0; yi < y.length; yi++) {
      for (let xi = 0; xi < x.length; xi++) {
        X.push(x[xi]);
        Y.push(y[yi]);
        Z.push(zNet[yi][xi]);
        custom.push([callsRaw[yi][xi], putsRaw[yi][xi]]);
      }
    }

    return {
      type: 'scatter3d',
      mode: 'markers',
      x: X,
      y: Y,
      z: Z,
      customdata: custom,
      hovertemplate:
        'Strike=%{x}<br>DTE=%{y}<br>Net OI=%{z}<br>Calls=%{customdata[0]} | Puts=%{customdata[1]}<extra></extra>',
      marker: { size: 2, color: 'rgba(0,0,0,0)' }, // invisible markers
      showlegend: false,
      hoverlabel: { bgcolor: 'rgba(0,0,0,0.8)' }
    };
  }

  // Mesh-based vertical plane for better visibility and depth sorting
  private buildUnderlyingPlaneMesh(
    underlyingPrice: number | undefined,
    x: number[],
    y: number[],
    zRef: number[][]
  ) {
    if (!underlyingPrice || !isFinite(underlyingPrice) || x.length === 0 || y.length === 0) {
      return null;
    }

    // Clamp plane x into current axis range (prevents it from being outside after filtering)
    const xmin = Math.min(...x);
    const xmax = Math.max(...x);
    const xp = Math.min(Math.max(underlyingPrice, xmin), xmax);

    // Z range reference
    const { zMin, zMax } = this.getZMinMax(zRef);
    const yMin = Math.min(...y);
    const yMax = Math.max(...y);

    // Four vertices of the plane
    const vx = [xp, xp, xp, xp];
    const vy = [yMin, yMax, yMax, yMin];
    const vz = [zMin, zMin, zMax, zMax];

    return {
      type: 'mesh3d',
      x: vx,
      y: vy,
      z: vz,
      i: [0, 0],
      j: [1, 2],
      k: [2, 3],
      name: 'Underlying',
      color: 'rgba(200,0,0,1)',
      opacity: 0.5,
      flatshading: true,
      hoverinfo: 'skip',
      showscale: false
    };
  }

  private getZMinMax(z: number[][]): { zMin: number; zMax: number } {
    let zMin = Number.POSITIVE_INFINITY;
    let zMax = Number.NEGATIVE_INFINITY;
    for (const row of z) {
      for (const v of row) {
        if (v < zMin) zMin = v;
        if (v > zMax) zMax = v;
      }
    }
    if (!isFinite(zMin) || !isFinite(zMax)) {
      zMin = 0;
      zMax = 1;
    }
    if (zMin === zMax) {
      zMin = 0;
      zMax = Math.max(1, zMax);
    }
    return { zMin, zMax };
  }

  private combineForRange(a: number[][] | null, b: number[][] | null): number[] {
    if (!a && !b) return [];
    const source = a ?? b;
    if (!source) return [];
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    source.forEach(row => row.forEach(v => {
      if (isFinite(v)) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }));
    return [min, max];
  }

  private buildUnderlyingPlaneTrace(underlyingPrice: number | undefined, y: number[] | null, zRange: number[]): any {
    if (!isFinite(underlyingPrice ?? NaN) || !y || !zRange || zRange.length < 2) {
      return undefined;
    }

    const z = [
      [zRange[0], zRange[0]],
      [zRange[1], zRange[1]]
    ];

    return {
      x: [underlyingPrice, underlyingPrice],
      y: [y[0], y[y.length - 1]],
      z: z,
      showscale: false,
      type: 'surface',
      colorscale: [[0, 'rgb(128,128,128)'], [1, 'rgb(128,128,128)']],
      opacity: 0.2,
      name: 'Underlying Price',
      hovertemplate: 'Underlying Price = %{x}<extra></extra>'
    };
  }

  private buildOISurfaces(
    chain: any,
    includeCalls: boolean,
    includePuts: boolean
  ): { x: number[]; y: number[]; zCalls: number[][]; zPuts: number[][]; zNet: number[][]; callsRaw: number[][]; putsRaw: number[][] } {
    const maps: { calls?: any; puts?: any } = {};
    if (includeCalls && chain?.callExpDateMap) maps.calls = chain.callExpDateMap;
    if (includePuts && chain?.putExpDateMap) maps.puts = chain.putExpDateMap;

    if (!maps.calls && !maps.puts) {
      throw new Error('No option data found in response');
    }

    const dteSet = new Set<number>();
    const strikeSet = new Set<number>();
    const parseDte = (expKey: string) => {
      const parts = expKey.split(':');
      if (parts.length > 1 && !isNaN(Number(parts[1]))) {
        return Number(parts[1]);
      }
      const dt = new Date(parts[0]);
      const now = new Date();
      const msPerDay = 24 * 60 * 60 * 1000;
      return Math.max(0, Math.round((dt.getTime() - now.getTime()) / msPerDay));
    };

    const harvestAxes = (map: any) => {
      Object.keys(map).forEach((expKey) => {
        const dte = parseDte(expKey);
        dteSet.add(dte);
        const strikesObj = map[expKey];
        Object.keys(strikesObj).forEach((strikeKey) => {
          const strike = Number(strikeKey);
          if (!isNaN(strike)) strikeSet.add(strike);
        });
      });
    };

    if (maps.calls) harvestAxes(maps.calls);
    if (maps.puts) harvestAxes(maps.puts);

    // Filter to nearest N expirations (ascending DTE)
    const desiredN: number = Number(this.optionForm.get('nearestExpirations')?.value) || 0;
    let yAll = Array.from(dteSet).sort((a, b) => a - b);
    if (desiredN > 0 && yAll.length > desiredN) {
      yAll = yAll.slice(0, desiredN);
    }

    // Strike selection: take strikeCount around ATM by underlying price
    const underlying = Number(chain?.underlyingPrice);
    const strikeCount: number = Math.max(0, Number(this.optionForm.get('strikeCount')?.value) || 0);
    const xAll = Array.from(strikeSet).sort((a, b) => a - b);
    let x: number[] = xAll;
    if (isFinite(underlying) && strikeCount > 0 && xAll.length > 0) {
      // pick closest strike index
      let closestIdx = 0;
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i < xAll.length; i++) {
        const diff = Math.abs(xAll[i] - underlying);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      const start = Math.max(0, closestIdx - strikeCount);
      const end = Math.min(xAll.length, closestIdx + strikeCount + 1);
      x = xAll.slice(start, end);
    }

    const y = yAll;

    const zCalls: number[][] = new Array(y.length).fill(0).map(() => new Array(x.length).fill(0));
    const zPuts: number[][] = new Array(y.length).fill(0).map(() => new Array(x.length).fill(0));
    const callsRaw: number[][] = new Array(y.length).fill(0).map(() => new Array(x.length).fill(0)); // positive calls OI
    const putsRaw: number[][] = new Array(y.length).fill(0).map(() => new Array(x.length).fill(0));  // positive puts OI

    const yIndex = new Map<number, number>(y.map((v, i) => [v, i]));
    const xIndex = new Map<number, number>(x.map((v, i) => [v, i]));

    const accumulate = (map: any, isPuts: boolean) => {
      Object.entries(map).forEach(([expKey, strikesObj]) => {
        const dte = parseDte(expKey);
        const yi = yIndex.get(dte);
        if (yi === undefined) return;

        Object.entries<any>(strikesObj as any).forEach(([strikeKey, contracts]) => {
          const strike = Number(strikeKey);
          const xi = xIndex.get(strike);
          if (xi === undefined) return;

          const oiSum = (contracts as any[]).reduce((acc, c) => acc + (Number(c?.openInterest) || 0), 0);
          if (isPuts) {
            zPuts[yi][xi] += -oiSum; // invert
            putsRaw[yi][xi] += oiSum;
          } else {
            zCalls[yi][xi] += oiSum;
            callsRaw[yi][xi] += oiSum;
          }
        });
      });
    };

    if (maps.calls) accumulate(maps.calls, false);
    if (maps.puts) accumulate(maps.puts, true);

    const zNet: number[][] = zCalls.map((row, i) => row.map((v, j) => v + zPuts[i][j]));

    return { x, y, zCalls, zPuts, zNet, callsRaw, putsRaw };
  }

  resetForm() {
    this.optionForm.reset({
      symbol: 'SPY',
      includeCalls: true,
      includePuts: true,
      fromDate: this.getTodayISO(),
      toDate: this.getDate30DaysFromToday(),
      strikeCount: 20,
      nearestExpirations: 6,
    });
    this.dataDisplayForm.reset({
      showRawData: false,
      showCalls: true,
      showPuts: true,
      showNet: true,
      showPlane: true,             // keep ON by default
      showCombinedTooltips: false, // keep OFF by default
    });
    this.graphData = undefined;
    this.graphLayout = undefined;
    this.rawChain = null;
    this.errorMsg = '';

    this.lastX = this.lastY = null;
    this.lastZCalls = this.lastZPuts = this.lastZNet = null;
    this.lastUnderlyingPrice = null;
    this.lastTitleSuffix = '';
    this.lastCallsRaw = this.lastPutsRaw = null;
  }

  private getTodayISO(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getDate30DaysFromToday(): string {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 30);

    const year = futureDate.getFullYear();
    const month = String(futureDate.getMonth() + 1).padStart(2, '0');
    const day = String(futureDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
